// امروز — the day sheet and the waiting room.
//
// THIS IS THE CLINIC CONNECTION. Every row is the same `appointments` row the
// منشی created in the clinic app: one table in scheduling, `doctor_id` = this
// dentist. Nothing here writes — booking, cancelling, marking arrived and queue
// order belong to the clinic desk, and the BFF exposes no scheduling mutation
// at all rather than shipping buttons that lead nowhere.
//
// Two queries, always fired together:
//   today(date, clinicId)  the selected calendar day, ascending
//   queue                  arrived + in_visit right now, in the SERVICE's order
//
// The queue is deliberately a separate read: it is "who is in the building",
// which is a fact about NOW and not about the selected day, and its order is
// the scheduling service's own (in_visit first, then the desk's manual
// position, then time). Re-deriving that order client-side is how the dentist
// and the desk end up disagreeing about who walks in next.

import React from 'react';
import { bffQuery } from '../lib/bffClient.js';
import { todayInIran } from '../lib/iranTime.js';

const APPOINTMENT_FIELDS = `
  id
  patientId
  placeholderLabel
  clinicId
  dateTime
  type
  status
  urgency
  queuePosition
  durationMinutes
  arrivedAt
  visitStartedAt
  patient { id name phone avatarUrl }
`;

export const DAY_QUERY = `
  query DaySheet($date: String, $clinicId: ID) {
    today(date: $date, clinicId: $clinicId) {
      date
      total
      appointments { ${APPOINTMENT_FIELDS} }
    }
  }
`;

// Scoped to the SAME clinic as the day sheet above. scheduling's
// GetQueueStatus filters on doctor_id only, so an unscoped queue merges every
// clinic this dentist works at into one waiting room — and the first row of it
// is what the screen calls «نفر بعدی». A day sheet showing clinic A next to a
// waiting room containing clinic B is worse than either alone.
export const QUEUE_QUERY = `
  query Queue($clinicId: ID) {
    queue(clinicId: $clinicId) { ${APPOINTMENT_FIELDS} }
  }
`;

// A day sheet goes stale by itself: the desk marks patients arrived while the
// dentist is looking at it. There is no /ws/events on this BFF — the doctor
// app's socket has no counterpart here — so this polls, and says so rather than
// opening a socket that would never connect.
const POLL_MS = 30_000;

export function useToday({ date, clinicId }) {
  const iso = date || todayInIran();

  const [appointments, setAppointments] = React.useState([]);
  const [queue, setQueue] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  // The day the newest request was fired for. A slow response for a day the
  // dentist has already navigated away from must not overwrite the one on
  // screen.
  const wantedRef = React.useRef(iso);
  wantedRef.current = iso;

  const load = React.useCallback(
    async ({ initial } = {}) => {
      if (initial) setLoading(true);
      const asked = iso;
      const [day, room] = await Promise.allSettled([
        bffQuery(DAY_QUERY, { date: asked, clinicId: clinicId || null }),
        bffQuery(QUEUE_QUERY, { clinicId: clinicId || null }),
      ]);

      if (wantedRef.current !== asked) return;

      if (day.status === 'fulfilled') {
        setAppointments(day.value?.today?.appointments || []);
      }
      if (room.status === 'fulfilled') {
        setQueue(room.value?.queue || []);
      }

      // The DAY read IS the screen, so a failed first paint of it is an error
      // screen even when the queue arrived.
      //
      // Requiring BOTH to fail looked conservative and was not: Today.web.jsx
      // gates the waiting-room block on the day list being non-empty, so a
      // queue that arrived alone paints nothing at all — the dentist is shown
      // «هیچ نوبتی برای این روز ثبت نشده» for a day that was never read, while
      // patients sit in the waiting room. "No appointments" and "we could not
      // ask" are different facts and only one of them is silence.
      //
      // A failed queue read on its own is NOT an error: it is supplementary,
      // and the day still renders. And `initial` still guards the other rule —
      // a failed background refresh never replaces a day the dentist is
      // reading with an error card.
      if (initial && day.status === 'rejected') {
        console.error('[useToday] day read failed:', day.reason?.message);
        setError(day.reason);
      } else if (day.status === 'fulfilled') {
        setError(null);
      }

      if (initial) setLoading(false);
    },
    [iso, clinicId],
  );

  React.useEffect(() => {
    let alive = true;
    setError(null);
    load({ initial: true });
    const timer = setInterval(() => {
      if (alive && document.visibilityState === 'visible') load();
    }, POLL_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [load]);

  return { appointments, queue, loading, error, reload: () => load({ initial: true }) };
}

export default useToday;
